import Vue from 'vue'
import { Helper } from '@/services/helper/helper'
import { AdServiceHelper } from '@/services/helper/adServiceHelper'
import Bubble from '@/components/Bubble/Bubble'
import Link from '@/components/Link/Link'
import Teaser from '@/components/Teaser/Teaser'
import { IMAGE_DIMENSIONS } from '@/components/Image/Image'
import { CAPTION_COLORS, CAPTION_POSITION, LABEL_COLORS } from '@/services/builder/Teaser'
import store from '@/store/AppStore'

export const AD_BLOCKER_TYPES = {
  none: 0,
  trackingBlocker: 1,
  adBlocker: 2
}

/**
 * Class providing functions for communication with AdService 2.
 * Is used static. Do not instantiate!
 * @abstract
 */
class AdServiceWrapper {
  /**
   * Initializes all the data.
   * @static
   * @param {string} dataName - The value for data attributes [data-[dataName]-slot].
   * @param {object} additionalParams - Additional Parameters for AdService 2 initialization.
   * @param {number} colCount - Current column state of homepage. Needed for statistics on AdServer.
   */
  static init (dataName, additionalParams, colCount) {
    if (dataName && typeof dataName === 'string' && dataName.length > 0) {
      this.setDataName(dataName)
    }

    const connectorUrl = AdServiceHelper.getScriptBaseUrl()
    const basicConfig = {
      'nonfriendlyiframe-document': connectorUrl + 'nonfriendlyiframe.html',
      'adservice-iframe-script-url': connectorUrl + 'connector.min.js',
      'request-ad-only-if-visible': false,
      'secure': true,
      'request-async': true
    }
    this.setConfig(basicConfig)
    this.initParams(additionalParams, colCount)

    AdServiceHelper.listenEvent('initialize.end', () => {
      this.setRenderTemplateCallback()
      window.AdService.Module('uim-cookie-exchanger').run()

      if (!window.logout) {
        this.incrementAc()
        AdServiceHelper.getHomepagePreBidConfig()
      }
    })
  }

  /**
   * Sets the data-[dataName]-slot for Ad Containers.
   * @static
   * @param {string} dataName - The attribute name.
   */
  static setDataName (dataName) {
    this.setConfig({
      'data-attribute-namespace': dataName
    })
  }

  /**
   * Sets configuration for AdService 2.
   * @static
   * @param {object} confObject - The object with the configuration fields and values.
   */
  static setConfig (confObject) {
    AdServiceHelper.listenEvent('initialize.end', () => {
      window.AdService.setConfig(confObject)
    })
  }

  /**
   * Listens to AdService 2 event which executes the 3rd -> 1st party cookie exchange.
   * Then reads the 1st party cookie and calls the callback function with nguserid as parameter.
   * @static
   * @param {callback} func - The callback function to call which gets the nguserid.
   */
  static getNgUserIdCookie () {
    return new Promise((resolve) => {
      AdServiceHelper.listenEvent('initialize.end', () => {
        if (typeof window.UI_nguserid === 'undefined') {
          AdServiceHelper.listenEvent('module.uim-cookie-exchanger.cookie-set', () => {
            const nguserid = Helper.getCookieValue('NGUserID')
            resolve(nguserid)
          })
        } else {
          const nguserid = Helper.getCookieValue('NGUserID')
          resolve(nguserid)
        }
      })
    })
  }

  /**
   * Sets the CSS for the iframe slots.
   * @static
   * @param {string} slot - The slot name to set the CSS for.
   */
  static setIframeCss (slot) {
    /**
     * Variable definition to be used for AdService.setIframeCss.
     * @param {string} action - The function to be called internal in AdService. Should always be 'useIframeCss' in this case.
     * @param {string} css - The name of the CSS. Used in AdService.setIframeCss as attribute name.
     */
    const top = {
      rules: [
        {
          action: 'useIframeCss',
          css: 'top'
        }
      ]
    }

    const middle = {
      rules: [
        {
          action: 'useIframeCss',
          css: 'middle'
        }
      ]
    }

    const left = {
      rules: [
        {
          action: 'useIframeCss',
          css: 'left'
        }
      ]
    }

    const right = {
      rules: [
        {
          action: 'useIframeCss',
          css: 'right'
        }
      ]
    }

    window.AdService.setIframeCss({
      top: {
        css: 'html, body, body > div { width: 100%; height: 100% } iframe, a { float: right }'
      },
      middle: {
        css: `html, body, body > div { width: 100%; height: 100% }
					iframe,
					body > div > a,
					body > div > div {
						display: inline-block !important;
						position: absolute !important;
						left: 50% !important;
						transform: translateX(-50%) !important;
					}`
      },
      left: {
        css: 'html, body, body > div { width: 100%; height: 100% } iframe, a { float: right }'
      },
      right: {
        css: 'html, body, body > div { width: 100%; height: 100% } iframe, a { float: left }'
      }
    })

    window.AdService.on('ad.request', slot, () => {
      let exclusionRules = {}

      if (slot === 'top') {
        exclusionRules.superbanner = top
      } else if (slot === 'middle') {
        exclusionRules.billboard = middle
      } else if (slot === 'left') {
        exclusionRules.skyRight = left
        exclusionRules.sitebar = left
        exclusionRules.halfpage = left
      } else if (slot === 'right') {
        exclusionRules.skyRight = right
        exclusionRules.sitebar = right
        exclusionRules.halfpage = right
      }

      window.AdService.setExclusionRuleSet(exclusionRules)
    })
  }

  /**
   * Initializes parameters needed by AdService2 and AdServer to display correct ads.
   * @static
   * @param {object} additionalParams - Can be given if there are additional parameters under certain circumstances.
   * @param {number} colCount - Current column count.
   */
  static async initParams (additionalParams, colCount) {
    const params = {}
    params.wi = store.state.adServiceParams.wi
    params.section = store.state.adServiceParams.section
    params.portal = store.state.adServiceParams.portal
    params.category = store.state.adServiceParams.category
    params.layoutclass = store.state.adServiceParams.layoutclass
    params.nguserid = store.state.adServiceParams['nguserid']
    params.consentlevel = store.state.adServiceParams['prf[consentLevel]']
    params.weather_condition = store.state.adServiceParams['prf[weather_condition]']
    params.weather_temp = store.state.adServiceParams['prf[weather_temp]']
    params.pagev = 2
    if (store.state.adServiceParams['prf[busterid]']) {
      params.hpeventid = store.state.adServiceParams['prf[busterid]']
    }

    if (window.PageConfig.sitespect) {
      params['prf[special]'] = +window.PageConfig.sitespect.special
    }

    if (window.PageConfig.site !== 'll') {
      if (!colCount) {
        // wait for colCount being set
        await new Promise((resolve) => {
          document.addEventListener('colCountChanged', (event) => {
            colCount = event.detail.colCount
            resolve()
          })
        })
      }

      params.categorytype = store.state.adData.type + '_' + colCount
    } else {
      this.setParamsIfSet(params, {
        pa: store.state.adServiceParams.pa,
        pg: store.state.adServiceParams.pg,
        pp: store.state.adServiceParams.pp,
        hid: store.state.adServiceParams.hid,
        userstatus: store.state.adServiceParams.userstatus,
        userlevel: store.state.adServiceParams.userlevel,
        deviceclient: store.state.adServiceParams.deviceclient,
        special: store.state.adServiceParams.special
      })
    }

    let initParams = Helper.shallowClone(params)

    if (Helper.isObject(additionalParams)) {
      initParams = Helper.mergeObjects(params, additionalParams)
    }
    AdServiceHelper.listenEvent('initialize.end', () => {
      window.AdService.setParam(initParams)
      window.AdService.triggerOnce('begin.config')
    })
  }

  /**
   * Adds parameters to the given object is the values are truethy so we don't overwrite possibly existing values on adservice
   * @static
   * @param {object} paramsObject - The target object where the parameters are set to
   * @param {object} paramsToAdd - The source object which holds the new values
   */
  static setParamsIfSet (paramsObject, paramsToAdd) {
    Object.keys(paramsToAdd).forEach((key) => {
      if (paramsToAdd[key]) {
        paramsObject[key] = paramsToAdd[key]
      }
    })
  }

  /**
   * Calls AdService2 to make an ad for a given slot.
   * @static
   * @param {string} slot - The slot name to make ad for.
   * @param {boolean} [setIframeCss] - If the iFrame CSS should be applied.
   */
  static makeAd (slot, setIframeCss) {
    if (setIframeCss) {
      this.setIframeCss(slot)
    }

    const slotElement = document.querySelector(`[data-service-slot="${slot}"]`)
    const slotSection = Helper.getElementByAttribute(slotElement, '[data-name]')

    // add channel name to params
    const channelName =
      slotSection?.previousElementSibling?.querySelector('h2')?.innerText ||
      // for channels with two sections (e.g. Ratgeber) we have to go back 2 siblings
      slotSection?.previousElementSibling?.previousElementSibling?.querySelector('h2')?.innerText
    if (channelName) {
      window.AdService.push(() => {
        window.AdService.AdSlot(slot).setParam('hpchannel', channelName.toLowerCase())
      })
    }

    const hasSubtext = typeof Helper.getAttributeValue(slotElement, 'data-has-subtext') === 'string' ? 1 : 0
    window.AdService.push(() => {
      window.AdService.AdSlot(slot).setParam('subtext', hasSubtext)
    })

    window.AdService.push(slot)
  }

  /**
   * Logs the current AdService2 version.
   * @static
   */
  static logVersion () {
    AdServiceHelper.listenEvent('initialize.end', () => {
      const versionObj = window.AdService.version
      console.log('[AdService] Version: ' + versionObj.major + '.' + versionObj.minor + '.' + versionObj.revision)
    })
  }

  /**
   * set the render callback for the AdService for different kinds of ads
   * @static
   */
  static setRenderTemplateCallback () {
    window.AdService.setConfig('render-template-callback', (renderObject) => {
      if (Array.isArray(renderObject.render)) {
        return renderObject.render.map(AdServiceWrapper.renderAds).join('')
      }
      return AdServiceWrapper.renderAds(renderObject.render)
    })

    // this part is responsible for setting the right image height
    // it only reacts to elements being rendered with AdService.render() which are only native teasers and the partner links at the moment
    // so we just check if the slot contains box_ or slider_ so we know we only have native teasers
    // we now have to make it this way, because we do not have a reference to the original element anymore
    window.AdService.on('render.end', (event) => {
      if (event?.data?.slot?.id.match(/^(box_|slider_|oms_)/)) {
        const cropImage = (img) => {
          img.style.height = ''
          let sixteenbynine = (img.clientWidth / 16) * 9 // Bildhöhe anhand der Breite im 16:9 Format berechnen
          const height = Math.ceil(sixteenbynine / 10) * 10 // 16:9 Höhe auf durch 10 teilbaren Wert erhöhen
          if (height > 1) {
            img.style.height = height + 'px'
          }
        }

        let img = event.data.slot.getDomNode().querySelector('img')
        cropImage(img)

        document.addEventListener('colCountChanged', () => {
          cropImage(img)
        })
      }
    })
  }

  /**
   * render the different type of ads
   * @param {object} renderData
   * @returns {string}
   */
  static renderAds (renderData) {
    switch (renderData._type) {
      case 'teaser-hs-image': // fallthrough
      case 'content-slider-news':
        let caption = null
        let label = null

        if (renderData._type === 'teaser-hs-image') {
          label = {
            color: LABEL_COLORS.greyLight,
            highlight: 'Anzeige',
            text: renderData.partner
          }
        } else if (renderData.partner || renderData.highlight) {
          label = {
            color: LABEL_COLORS.default,
            highlight: renderData.highlight,
            text: renderData.partner
          }
        }

        if (renderData.caption) {
          caption = {
            text: renderData.caption.text,
            icon: renderData.caption.icon || null,
            color: renderData.caption.color || CAPTION_COLORS.accent,
            position: renderData.caption.position || CAPTION_POSITION.top
          }
        }

        const TeaserClass = Vue.extend(Teaser)
        const teaserInstance = new TeaserClass({
          propsData: {
            link: {
              href: renderData.clickurl,
              target: renderData.target
            },
            figure: {
              img: {
                url: renderData.imageurl,
                alt: renderData.headline?.replace(/(##br##)/g, '\n'),
                width: IMAGE_DIMENSIONS.teaser.width,
                height: IMAGE_DIMENSIONS.teaser.height
              },
              caption
            },
            article: {
              header: {
                label,
                headline: renderData.headline?.replace(/(##br##)/g, '\n'),
                isDivTag: true
              },
              summary: renderData.subline?.replace(/(##br##)/g, '\n'),
              content: renderData.subline?.replace(/(##br##)/g, '\n')
            }
          }
        })

        teaserInstance.$mount()
        const img = teaserInstance.$el.querySelector('img')

        if (img.dataset.src) {
          img.src = img.dataset.src
        }

        const articleSummary = teaserInstance.$el.querySelector('p')
        if (articleSummary) {
          articleSummary.style.whiteSpace = 'pre-line'
        }

        if (renderData.count) {
          for (let i = 0; i < renderData.count.length; i++) {
            new Image().src = renderData.count[i]
          }
        }

        return teaserInstance.$el.outerHTML
      case 'partner-link':
        const a = document.createElement('a')
        a.href = renderData.clickurl
        a.classList.add('partner-link')
        a.target = '_blank'
        a.rel = 'sponsored'

        const image = document.createElement('img')
        image.src = renderData.imageurl

        a.appendChild(image)

        return a.outerHTML
      case 'bubble':
        const BubbleClass = Vue.extend(Bubble)
        const bubbleInstance = new BubbleClass({
          propsData: {
            href: renderData.clickUrl,
            imageUrl: renderData.imageUrl,
            iconUrl: renderData.dynamic?.iconUrl,
            text: renderData.dynamic?.text,
            textColor: renderData.dynamic?.textColor,
            backgroundColor: renderData.dynamic?.backgroundColor
          }
        })

        bubbleInstance.$mount()

        if (renderData.count) {
          for (let i = 0; i < renderData.count.length; i++) {
            new Image().src = renderData.count[i]
          }
        }

        /**
         * add the Vue-Component after a short timeout, because otherwise the html will be overwritten by AdService
         * if we return only the html of the component, the functionality will be lost
         */
        setTimeout(() => {
          document.querySelector(`[data-service-slot="${renderData.slot.id}"]`)?.appendChild(bubbleInstance.$el)
        }, 0)

        return ''
      case 'linklist':
        const LinkClass = Vue.extend(Link)
        const linkInstance = new LinkClass({
          propsData: {
            title: renderData.text,
            url: renderData.clickurl,
            external: true,
            behaviour: 'sponsored',
            image: {
              url: renderData.imageurl,
              alt: renderData.text,
              lazy: true,
              width: IMAGE_DIMENSIONS.icon.width,
              height: IMAGE_DIMENSIONS.icon.height
            }
          }
        })

        linkInstance.$mount()

        if (renderData.count) {
          for (let i = 0; i < renderData.count.length; i++) {
            new Image().src = renderData.count[i]
          }
        }

        return linkInstance.$el.outerHTML
      default:
        return ''
    }
  }

  /**
   * Commits HTML to AdService2 which queues it to put it into an iFrame.
   * @static
   * @param {string} slot - The Slot to queue the HTML for.
   * @param {string} htmlSnippet - The HTML snippet as string.
   * @param {boolean} refresh - If the slot should be refreshed. Should be true if there's already an ad in there.
   * @param {string} actualAdType - Sometimes it's necessary to insert the actual AdType in here
   */
  static queueHtml (slot, htmlSnippet, refresh, actualAdType, iframeCSS = true) {
    AdServiceHelper.listenEvent('initialize.end', () => {
      if (iframeCSS) {
        this.setIframeCss(slot)
      }

      window.AdService.AdSlot(slot).queueAdHtml(htmlSnippet, actualAdType)
      window.AdService.AdSlot(slot).setFixedSize(false)

      if (refresh) {
        window.AdService.refresh(slot)
      } else {
        window.AdService.ad(slot)
      }
    })
  }

  /**
   * Returns a XML Tag uim:ad with information for AdService2.
   * @static
   * @param {string} slot - The slot to return the Tag for.
   * @param {number} fallbackWidth - The fallback width for the Ad.
   * @param {number} fallbackHeight - The fallback height for the Ad.
   * @returns {string} The Tag as a string.
   */
  static getUIMTag (slot, fallbackWidth, fallbackHeight) {
    return store.state.adData.uimAd || this.createUimTag(slot, fallbackWidth, fallbackHeight)
  }

  /**
   * Creates a XML Tag uim:ad with the size attribute filled.
   * @static
   * @param {string} slot - The slot to create the Tag for.
   * @param {number} fallbackWidth - The fallback width that should be inserted.
   * @param {number} fallbackHeight - The fallback height that should be inserted.
   * @returns {string} The Tag as a string.
   */
  static createUimTag (slot, fallbackWidth, fallbackHeight) {
    const { adData } = store.state

    let useWidth
    let useHeight

    const billboard = {
      width: 970,
      height: 250
    }
    const sky = {
      width: 160,
      height: 600
    }
    const sitebar = {
      width: 220,
      height: 600
    }

    if (this.fallback) {
      useWidth = fallbackWidth
      useHeight = fallbackHeight
    } else {
      switch (adData.type) {
        case 'cinematic':
        case 'billboard':
          useWidth = billboard.width
          useHeight = billboard.height
          break
        case 'sitebar':
        case 'double-sitebar':
          useWidth = sitebar.width
          useHeight = sitebar.height
          break
        case 'bridge':
          if (slot === 'middle') {
            useWidth = billboard.width
            useHeight = billboard.height
            break
          }
          if (slot === 'left' || slot === 'right') {
            if ((adData.skyVariant !== undefined && adData.skyVariant.length === 0) || adData.skyVariant === 'dynamic') {
              useWidth = sitebar.width
              useHeight = sitebar.height
              break
            } else {
              useWidth = sky.width
              useHeight = sky.height
              break
            }
          }
          break
        case 'flagship':
          if (slot === 'middle') {
            useWidth = billboard.width
            useHeight = billboard.height
            break
          }
          if (slot === 'right') {
            useWidth = sitebar.width
            useHeight = sitebar.height
            break
          }
          break
        default:
      }
    }

    return `<uim:ad adsize="${useWidth}x${useHeight}"></uim:ad>`
  }

  /**
   * Computes fallback condition given in initial ad call to see if a fallback should be shown.
   * @static
   * @returns {boolean} State if fallback should be shown.
   */
  static showFallback () {
    const fallbackConditions = store.state.adData.showFallback
    if (!fallbackConditions) {
      this.fallback = false
      return false
    }

    if (fallbackConditions === true || fallbackConditions === 'true') {
      this.fallback = true
      return true
    }

    let consentFallback

    if (Helper.getCookieValue('euconsent-v2')) {
      consentFallback = !!fallbackConditions?.tcf
      try {
        const consents = []

        Object.entries(fallbackConditions.tcf).forEach(([key, value]) => {
          window.__tcfapi('getPermission', 2, (valid) => consents.push(!valid), key, value)
        })

        consentFallback = consents.some((consent) => consent)
      } catch (e) {
        // any error, mostly some data isn't set or tcfapi is not available
      }
    } else {
      consentFallback = !window.PageConfig.optin
    }

    const agent = store.state.agent

    const browserFallback = agent?.browserVersion <= fallbackConditions?.browser?.[agent?.browser] // Test if browser is even in fallback liste and if so, check if the version is smaller or equal to the fallback condition
    const osFallback = fallbackConditions?.os?.[agent?.os] === true // Test if the os is true for full os fallback
    const osVersionFallback = agent?.osVersion <= fallbackConditions?.os?.[agent?.os] // Test if the os version is lower or equal to the fallback condition
    const cleanFallback = !!(!window.availableResponse && fallbackConditions?.clean)

    const showFallback = !!(browserFallback || osFallback || osVersionFallback || cleanFallback || consentFallback) /* || true */ // If you want to always have a fallback uncomment the true
    this.fallback = showFallback

    return showFallback
  }

  /**
   * Calls AdService2 function to check if AdBlocker is detected.
   * @static
   * @param {callback} callBack - Called after AdBlocker Detection is done with the information parameter if AdBlocker was detected.
   * @fires Document#adbDone
   * @fires Document#bdDone
   */
  static checkAdBlocker () {
    return new Promise((resolve) => {
      AdServiceHelper.listenEvent('adblock.detectionfinished', () => {
        const isBlocked = !!window.AdService.info?.abd()?.details?.type?.css
        document.querySelector('html').setAttribute('data-is-blocked', isBlocked)

        if (isBlocked) {
          if (window.AdService.info?.abd()?.details?.fyi?.unitedBlocked) {
            window.AdService.setDefaultAdServer('adition-asp-info')
          } else {
            window.AdService.setDefaultAdServer('adition-asp-united-infos')
          }
        }

        Helper.fireEvent('abdDone', { abd: isBlocked })

        if (isBlocked) {
          Helper.fireEvent('bdDone', { abd: true, adBlockerType: AD_BLOCKER_TYPES.adBlocker })
        }

        resolve(isBlocked)
      })
    })
  }

  /**
   * Creates a script-Tag with src attribute pointing towards an 'available' ad slot.
   * If the loading of this src fails there is a tracking blocker active.
   * @static
   * @param {callback} callBack - Called after Tracking Blocker Detection is done with the information parameter if Tracking Blocker was detected.
   * @fires Document#tbdDone
   * @fires Document#bdDone
   */
  static checkTrackingBlocker () {
    return new Promise((resolve) => {
      const tbdDoneDetails = { abd: false }

      const loadingFailed = () => {
        this.setTrackingBlockerAdServerParams()

        tbdDoneDetails.adBlockerType = AD_BLOCKER_TYPES.trackingBlocker
        Helper.fireEvent('tbdDone', tbdDoneDetails)
        Helper.fireEvent('bdDone', tbdDoneDetails)

        resolve(true)
      }

      const imgTag = document.createElement('img')

      imgTag.onerror = () => {
        loadingFailed()
      }

      imgTag.onload = () => {
        window.availableResponse = true
        tbdDoneDetails.adBlockerType = AD_BLOCKER_TYPES.none

        Helper.fireEvent('tbdDone', tbdDoneDetails)
        Helper.fireEvent('bdDone', tbdDoneDetails)

        resolve(false)
      }

      imgTag.src = 'https://imagesrv.adition.com/1x1.gif'

      const availableContainer = document.querySelector('[data-available]')

      if (availableContainer) {
        availableContainer.appendChild(imgTag)
      } else {
        loadingFailed()
      }
    })
  }

  static setTrackingBlockerAdServerParams () {
    window.AdService.setDefaultAdServer('adition-asp-united-uimserv')
    window.AdService.setParam({ deviceclient: 'browser_clean' })
    window.availableResponse = false
  }

  /**
   * Loop to create the MedRecs.
   * This function is only used on HP, where the ads are not vue-components.
   * On LL the ads are vue-components and load the ad on mount
   * TODO: remove when ad vue-component is used on HP
   * @static
   * @see {@link AdServiceWrapper.makeAd}
   */
  static makeBoxAds () {
    for (let i = 1; i <= 10; i++) {
      document.querySelector(`[data-service-slot=box_${i}]`) && this.makeAd('box_' + i)
    }
  }

  /**
   * Loop to create the three partner links in Navigation Bar.
   * @static
   * @see {@link AdServiceWrapper.makeAd}
   */
  static makePartnerlinks () {
    for (let i = 1; i <= 3; i++) {
      document.querySelector(`[data-service-slot=promo_${i}]`) && this.makeAd('promo_' + i)
    }
  }

  /**
   * Loop to create the ads of Vorteilswelt.
   * @static
   * @see {@link AdServiceWrapper.makeAd}
   */
  static makeVorteilswelt () {
    if (window.PageConfig.vorteilswelt) {
      for (let i = 5; i >= 1; i--) {
        document.querySelector(`[data-service-slot=slider_1_${i}]`) && this.makeAd('slider_1_' + i)
      }
    }
  }

  static makeFooterLinklist () {
    document.querySelector('[data-service-slot="linklist"]') && this.makeAd('linklist')
  }

  /**
   * Increments the Ad Count in AdService2.
   * Initial AdCall is not counted correctly because it's in the backend so this is necessary before first AdCall in Frontend.
   * @static
   */
  static incrementAc () {
    AdServiceHelper.listenEvent('initialize.end', () => {
      window.AdService.getDefaultAdServer().increaseRequestCount()
    })
  }
}

export { AdServiceWrapper }
